import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { PartnerDestinationSelect } from "@/components/pages/partner-integration-settings/components/PartnerDestinationSelect";
import { useUpdatePartnerSettings } from "@/components/pages/partner-integration-settings/hooks/partner-settings.hooks";
import {
  startSyncWait,
  useSyncWaiting,
} from "@/components/pages/partner-integration-settings/utils/partnerDestinationSelect";
import { useHotel } from "@/hooks/useHotel";
import type {
  PartnerDestination,
  PartnerSettings,
} from "@/types/partner-settings";

export type GuesteriaPartnerEditRegionDialogProps = {
  trigger: React.ReactNode;
  settings: PartnerSettings;
  /** The dialog cannot be opened (Front Desk reads the city only). */
  disabled?: boolean;
};

function destinationFromSettings(
  settings: PartnerSettings,
): PartnerDestination | null {
  const id = settings.destinationId?.trim();
  const name = settings.destinationName?.trim();
  if (!id || !name) {
    return null;
  }
  return {
    destinationId: id,
    name,
    type: settings.destinationType?.trim() || "CITY",
  };
}

export function GuesteriaPartnerEditRegionDialog({
  trigger,
  settings,
  disabled = false,
}: GuesteriaPartnerEditRegionDialogProps): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<PartnerDestination | null>(null);

  const updatePartnerSettings = useUpdatePartnerSettings();
  const { hotelId } = useHotel();
  const queryClient = useQueryClient();
  // The wait for the activity sync is kept outside the component (per hotel),
  // so Save stays blocked after the dialog is closed and reopened.
  const syncWaiting = useSyncWaiting(hotelId);

  const syncFromSettings = React.useCallback(() => {
    setSelected(destinationFromSettings(settings));
  }, [settings]);

  useEffect(() => {
    if (open) {
      syncFromSettings();
    }
  }, [open, syncFromSettings]);

  const isSaving = updatePartnerSettings.isPending;
  const isBusy = isSaving || syncWaiting;
  const canSave = selected != null && !disabled && !isBusy;

  let busyText: string | null = null;
  if (isSaving) {
    busyText = t("partners.hotelSettings.city.saving");
  } else if (syncWaiting) {
    busyText = t("partners.hotelSettings.city.syncing");
  }

  const handleSave = () => {
    if (!selected || !canSave) {
      return;
    }
    const changedHotelId = hotelId;
    updatePartnerSettings
      .mutateAsync({
        destinationId: selected.destinationId,
        destinationName: selected.name,
        destinationType: selected.type,
      })
      .then(() => {
        if (changedHotelId) startSyncWait(changedHotelId, queryClient);
        toast.success(t("partners.hotelSettings.city.syncing"));
        setOpen(false);
      })
      .catch(() => {
        toast.error(t("partners.hotelSettings.city.saveError"));
      });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next && !disabled);
      }}
    >
      <DialogTrigger asChild>
        <span className="inline-flex shrink-0">{trigger}</span>
      </DialogTrigger>

      <DialogContent
        showCloseButton={false}
        className="flex max-h-[min(90vh,640px)] w-[calc(100%-2rem)] max-w-[360px] flex-col gap-0 overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white p-0 shadow-[0px_3px_3px_0px_rgba(0,0,0,0.12)] sm:max-w-[360px]"
      >
        <div className="relative flex shrink-0 flex-col border-b border-[#E5E5E5] px-4 pt-4 pb-3">
          <DialogTitle className="text-center text-base leading-normal font-semibold text-[#171717]">
            {t("partners.detail.editRegion.title")}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("partners.detail.editRegion.formDescription")}
          </DialogDescription>
          <button
            type="button"
            className="absolute top-3 right-3 rounded p-1 text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-900"
            onClick={() => setOpen(false)}
            aria-label={t("partners.detail.editRegion.closeAria")}
          >
            <X className="size-5" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-4">
          <PartnerDestinationSelect
            id={`partners-region-${settings.id}`}
            currentName={selected?.name}
            busy={isBusy}
            busyText={busyText}
            onSelect={setSelected}
          />
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-[#E5E5E5] px-4 py-3">
          <Button
            type="button"
            variant="outline"
            className="h-8 rounded-md border-[#E5E5E5] bg-white px-4 text-sm font-medium text-zinc-900 shadow-none hover:bg-zinc-50"
            onClick={() => setOpen(false)}
          >
            {t("partners.detail.editRegion.cancel")}
          </Button>
          <Button
            type="button"
            disabled={!canSave}
            className="h-8 rounded-md bg-[#6D82A3] px-4 text-sm leading-normal font-medium text-[#FAFAFA] hover:bg-[#5F7597] disabled:opacity-50"
            onClick={handleSave}
          >
            {isSaving
              ? t("partners.saving")
              : t("partners.detail.editRegion.save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
